import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useConfigs } from '../../hooks/useConfigs'
import { useTenants } from '../../hooks/useTenants'
import { useInventorySystemProfiles } from '../../hooks/useInventorySystemProfiles'
import { useVenues } from '../../hooks/useVenues'
import { ConfigsTable } from '../../components/ConfigsTable/ConfigsTable'
import { ConfigFilterBar } from '../../components/ConfigFilterBar/ConfigFilterBar'
import { encodeConfigId } from '../../helpers/encodeConfigId'
import { ListEntityPage } from '../ListEntityPage/ListEntityPage'
import { readConfigFiltersFromParams } from './helpers/readConfigFiltersFromParams'
import { buildConfigsSearchParams } from './helpers/buildConfigsSearchParams'
import { buildConfigFilterOptions } from './helpers/buildConfigFilterOptions'
import type { ConfigFilters } from '../../types'

export function ConfigsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Applied filters live in the query string so the filtered view is shareable
  // and survives reloads. tenant/ISP/venue/configKey map to their API param
  // names; tags is percent-encoded (see buildConfigsSearchParams). useConfigs
  // threads them all to the API, where they AND-combine.
  const filters = readConfigFiltersFromParams(searchParams)

  const { configs, isLoading, error } = useConfigs(filters)

  // The tenant/ISP/venue dropdown options load independently of the list — the
  // list stays usable while they populate, so their loading/error state does not
  // gate the page shell.
  const { tenants } = useTenants()
  const { profiles } = useInventorySystemProfiles()
  const { venues } = useVenues()

  const { tenantOptions, ispOptions, venueOptions } = useMemo(
    () => buildConfigFilterOptions(tenants, profiles, venues),
    [tenants, profiles, venues]
  )

  function handleFilterChange(next: ConfigFilters) {
    setSearchParams(buildConfigsSearchParams(searchParams, next))
  }

  return (
    <ListEntityPage
      title="Configs"
      description="Key and value settings scoped to a tenant, an inventory system profile or a venue."
      loadingLabel="configs"
      isLoading={isLoading}
      error={error}
      createHref="/configs/create"
      createLabel="New config"
    >
      <ConfigFilterBar
        onChange={handleFilterChange}
        initialFilters={filters}
        tenantOptions={tenantOptions}
        ispOptions={ispOptions}
        venueOptions={venueOptions}
      />
      <ConfigsTable
        configs={configs}
        venues={venues}
        onRowClick={(c) => navigate(`/configs/${encodeConfigId(c)}`)}
      />
    </ListEntityPage>
  )
}
